/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { workloadIdentitiesUpdateIssuer } from "../funcs/workloadIdentitiesUpdateIssuer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { WorkloadIdentityPolicy } from "../models/components/workloadidentitypolicy.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateWorkloadIssuerRequest,
  UpdateWorkloadIssuerSecurity,
} from "../models/operations/updateworkloadissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateWorkloadIssuerMutationVariables = {
  request: UpdateWorkloadIssuerRequest;
  security?: UpdateWorkloadIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateWorkloadIssuerMutationData = WorkloadIdentityPolicy;

export type UpdateWorkloadIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateIssuer workloadIdentities
 *
 * @remarks
 * Edit a trusted issuer's name, description, tags, or JWKS URI. Omitted fields are left unchanged. The issuer URL and the wildcard admission setting are fixed at registration. Requires workload:write. Returns the whole policy, so a caller replaces its view rather than merging into it.
 */
export function useUpdateWorkloadIssuerMutation(
  options?: MutationHookOptions<
    UpdateWorkloadIssuerMutationData,
    UpdateWorkloadIssuerMutationError,
    UpdateWorkloadIssuerMutationVariables
  >,
): UseMutationResult<
  UpdateWorkloadIssuerMutationData,
  UpdateWorkloadIssuerMutationError,
  UpdateWorkloadIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateWorkloadIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateWorkloadIssuer(): MutationKey {
  return ["@gram/client", "workloadIdentities", "updateIssuer"];
}

export function buildUpdateWorkloadIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateWorkloadIssuerMutationVariables,
  ) => Promise<UpdateWorkloadIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateWorkloadIssuer(),
    mutationFn: function updateWorkloadIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateWorkloadIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(workloadIdentitiesUpdateIssuer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
